import { ChangesDetection } from "@adas/ng-overlay";
import { I_UnitResponse } from "../api/unit.response";
import { C_MasterList } from "../items/master-lists/domain/master-list.model";
import { C_UnitConfiguration } from "./unit-config.model";
import { Observable, startWith, filter, map, distinctUntilChanged, switchMap, of, shareReplay } from "rxjs";

export class C_Unit extends ChangesDetection<T_UnitChange> {
  id?: string;
  title?: string;
  name?: string;
  configuration: C_UnitConfiguration = new C_UnitConfiguration();

  // Listas maestras
  allergyList: C_MasterList = new C_MasterList();
  altableList: C_MasterList = new C_MasterList();
  destinationList: C_MasterList = new C_MasterList();
  accessControlList: C_MasterList = new C_MasterList();
  diagnosisList: C_MasterList = new C_MasterList();
  doctorList: C_MasterList = new C_MasterList();
  doctorTypeList: C_MasterList = new C_MasterList();
  insulationList: C_MasterList = new C_MasterList();
  mobilityList: C_MasterList = new C_MasterList();
  originList: C_MasterList = new C_MasterList();
  patientStatusList: C_MasterList = new C_MasterList();
  languageBarrierList: C_MasterList = new C_MasterList();
  passiveSittingList: C_MasterList = new C_MasterList();
  procedureList: C_MasterList = new C_MasterList();
  testList: C_MasterList = new C_MasterList();
  serviceList: C_MasterList = new C_MasterList();
  therapeuticCeilingList: C_MasterList = new C_MasterList();
  treatmentList: C_MasterList = new C_MasterList();
  visitList: C_MasterList = new C_MasterList();
  genericList: C_MasterList = new C_MasterList();
  carePlanObjectiveList: C_MasterList = new C_MasterList();
  episList: C_MasterList = new C_MasterList();
  hobbiesList: C_MasterList = new C_MasterList();
  nurseCallList: C_MasterList = new C_MasterList();
  alarmStatusList: C_MasterList = new C_MasterList();
  catheterList: C_MasterList = new C_MasterList(); // Sondas
  clinicalRouteList: C_MasterList = new C_MasterList(); // Vías
  clinicalRouteLocationList: C_MasterList = new C_MasterList();
  // Cache de observables activos
  private cachedSubs = new Map<T_UnitMasterListKeys, Observable<C_MasterList>>();

  constructor(data?: I_UnitResponse) {
    super();
    if (!data) return;
    this.initialize(data);
  }

  /**
   * Inicialización desde JSON del API
   */
  initialize(u: I_UnitResponse) {
    let metadataChanged = false;
    const fields: Partial<Pick<C_Unit, 'id' | 'title' | 'name'>> = {};

    if (u?.Id && this.id !== u.Id) { this.id = u.Id; fields.id = u.Id; metadataChanged = true; }
    if (u?.Title && this.title !== u.Title) { this.title = u.Title; fields.title = u.Title; metadataChanged = true; }
    if (u?.Name && this.name !== u.Name) { this.name = u.Name; fields.name = u.Name; metadataChanged = true; }

    if (metadataChanged) {
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateMetadata, fields });
    }

    // Inicialización granular de listas mediante ayudante privado
    this.updateListFromJson('carePlanObjectiveList', u?.CarePlanObjectiveList);
    this.updateListFromJson('accessControlList', u?.AccessControlList);
    this.updateListFromJson('allergyList', u?.AllergyList);
    this.updateListFromJson('altableList', u?.AltableList);
    this.updateListFromJson('destinationList', u?.DestinationList);
    this.updateListFromJson('diagnosisList', u?.DiagnosisList);
    this.updateListFromJson('doctorList', u?.DoctorList);
    this.updateListFromJson('doctorTypeList', u?.DoctorTypeList);
    this.updateListFromJson('insulationList', u?.InsulationList);
    this.updateListFromJson('mobilityList', u?.MobilityList);
    this.updateListFromJson('originList', u?.OriginList);
    this.updateListFromJson('patientStatusList', u?.PatientStatusList);
    this.updateListFromJson('procedureList', u?.ProcedureList);
    this.updateListFromJson('testList', u?.TestList);
    this.updateListFromJson('serviceList', u?.ServiceList);
    this.updateListFromJson('therapeuticCeilingList', u?.TherapeuticCeillingList); // Corrige diferencia de typo 'll' vs 'l'
    this.updateListFromJson('treatmentList', u?.TreatmentList);
    this.updateListFromJson('visitList', u?.VisitList);
    this.updateListFromJson('languageBarrierList', u?.LanguageBarrierList);
    this.updateListFromJson('passiveSittingList', u?.PassiveSittingList);
    this.updateListFromJson('genericList', u?.GenericList);
    this.updateListFromJson('episList', u?.EpisList);
    this.updateListFromJson('hobbiesList', u?.HobbiesList);
    this.updateListFromJson('catheterList', u?.CatheterList); // Sondas
    this.updateListFromJson('clinicalRouteList', u?.ClinicalRouteList); // Vías
    this.updateListFromJson('clinicalRouteLocationList', u?.ClinicalRouteLocationList);
    this.updateListFromJson('nurseCallList', u?.NurseCallList);
    this.updateListFromJson('alarmStatusList', u?.AlarmStatusList);

    if (u?.configuration) {
      this.configuration.initialize(u.configuration);
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateConfig });
    }
  }

  /**
   * Actualización parcial desde otro objeto C_Unit (proveniente de WebSockets)
   */
  updateFromUnit(u: C_Unit) {
    let metadataChanged = false;
    const fields: Partial<Pick<C_Unit, 'id' | 'title' | 'name'>> = {};

    if (u?.id && this.id !== u.id) { this.id = u.id; fields.id = u.id; metadataChanged = true; }
    if (u?.title && this.title !== u.title) { this.title = u.title; fields.title = u.title; metadataChanged = true; }
    if (u?.name && this.name !== u.name) { this.name = u.name; fields.name = u.name; metadataChanged = true; }

    if (metadataChanged) {
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateMetadata, fields });
    }

    // Actualización granular de listas
    this.updateListFromUnit('accessControlList', u?.accessControlList);
    this.updateListFromUnit('allergyList', u?.allergyList);
    this.updateListFromUnit('altableList', u?.altableList);
    this.updateListFromUnit('destinationList', u?.destinationList);
    this.updateListFromUnit('diagnosisList', u?.diagnosisList);
    this.updateListFromUnit('doctorList', u?.doctorList);
    this.updateListFromUnit('doctorTypeList', u?.doctorTypeList);
    this.updateListFromUnit('insulationList', u?.insulationList);
    this.updateListFromUnit('mobilityList', u?.mobilityList);
    this.updateListFromUnit('originList', u?.originList);
    this.updateListFromUnit('patientStatusList', u?.patientStatusList);
    this.updateListFromUnit('procedureList', u?.procedureList);
    this.updateListFromUnit('testList', u?.testList);
    this.updateListFromUnit('serviceList', u?.serviceList);
    this.updateListFromUnit('therapeuticCeilingList', u?.therapeuticCeilingList);
    this.updateListFromUnit('treatmentList', u?.treatmentList);
    this.updateListFromUnit('visitList', u?.visitList);
    this.updateListFromUnit('languageBarrierList', u?.languageBarrierList);
    this.updateListFromUnit('passiveSittingList', u?.passiveSittingList);
    this.updateListFromUnit('genericList', u?.genericList);
    this.updateListFromUnit('episList', u?.episList);
    this.updateListFromUnit('hobbiesList', u?.hobbiesList);
    this.updateListFromJson('catheterList', u?.catheterList); // Sondas
    this.updateListFromJson('clinicalRouteList', u?.clinicalRouteList); // Vías
    this.updateListFromJson('clinicalRouteLocationList', u?.clinicalRouteLocationList);
    this.updateListFromJson('nurseCallList', u?.nurseCallList);
    this.updateListFromJson('alarmStatusList', u?.alarmStatusList);

    if (u?.configuration) {
      this.configuration.initializeFromConfig(u.configuration);
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateConfig });
    }
  }

  /**
   * Obtiene el flujo dinámico optimizado y cacheado para una lista específica
   */
  getLiveList(listName: T_UnitMasterListKeys): Observable<C_MasterList> {
    if (this.cachedSubs.has(listName)) {
      return this.cachedSubs.get(listName)!;
    }

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Disparo inicial para que el componente obtenga el estado actual
        if (change.action === E_UnitActionChange.Reset) return true;
        return change.action === E_UnitActionChange.UpdateList && change.listName === listName;
      }),
      map(() => this[listName as keyof C_Unit] as C_MasterList),
      distinctUntilChanged(), // No vuelve a emitir si la instancia de la lista no ha cambiado de referencia
      switchMap((list) => {
        if (!list) return of(undefined);

        // Si C_MasterList también extiende de ChangesDetection, nos acoplamos a sus cambios internos
        if (typeof (list as any).getForcedChanges === 'function') {
          return (list as any).getForcedChanges().pipe(
            startWith(list),
            map(() => list)
          );
        }
        return of(list);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(listName, stream$ as Observable<C_MasterList>);
    return stream$ as Observable<C_MasterList>;
  }

  /**
   * Limpia las suscripciones en caché para evitar pérdidas de memoria
   */
  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_UnitActionChange.Reset });
  }

  /**
   * Helpers privados para automatizar la actualización y disparo selectivo
   */
  private updateListFromJson(key: keyof C_Unit, jsonData: any) {
    if (!jsonData) return;
    const list = this[key] as any;
    if (list && typeof list.initializeFromJson === 'function') {
      list.initializeFromJson(jsonData);
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateList, listName: key as T_UnitMasterListKeys });
    }
  }

  private updateListFromUnit(key: keyof C_Unit, otherList: any) {
    if (!otherList) return;
    const list = this[key] as any;
    if (list && typeof list.initializeFromList === 'function') {
      list.initializeFromList(otherList);
      this.forceUpdateDetection({ action: E_UnitActionChange.UpdateList, listName: key as T_UnitMasterListKeys });
    }
  }

  getListByName(nameToFound: string): C_MasterList | undefined {
    let listToReturn;
    Object.entries(this).forEach(([k, v]: [string, any]) => {
      if (k.toLowerCase() == nameToFound.toLowerCase()) {
        listToReturn = v;
      }
    })
    return listToReturn;
  }
}

export type T_UnitMasterListKeys = keyof I_MasterListNames;

export type T_UnitChange =
  | { action: E_UnitActionChange.UpdateMetadata; fields: Partial<Pick<C_Unit, 'id' | 'title' | 'name'>> }
  | { action: E_UnitActionChange.UpdateList; listName: T_UnitMasterListKeys }
  | { action: E_UnitActionChange.UpdateConfig }
  | { action: E_UnitActionChange.Reset };

export enum E_UnitActionChange {
  UpdateMetadata = 'UpdateMetadata',
  UpdateList = 'UpdateList',
  UpdateConfig = 'UpdateMetadata',
  Reset = 'Reset',
}
export interface I_MasterListNames {
  allergyList?: boolean;
  altableList?: boolean;
  destinationList?: boolean;
  accessControlList?: boolean;
  diagnosisList?: boolean;
  doctorList?: boolean;
  doctorTypeList?: boolean;
  insulationList?: boolean;
  mobilityList?: boolean;
  originList?: boolean;
  patientStatusList?: boolean;
  languageBarrierList?: boolean;
  passiveSittingList?: boolean;
  procedureList?: boolean;
  testList?: boolean;
  serviceList?: boolean;
  therapeuticCeilingList?: boolean;
  treatmentList?: boolean;
  visitList?: boolean;
  genericList?: boolean;
  carePlanObjectiveList?: boolean;
  episList?: boolean;
  hobbiesList?: boolean;
  catheterList?: boolean; // Sondas
  clinicalRouteList?: boolean; // Vías
  clinicalRouteLocationList?: boolean;
  nurseCallList?: boolean;
  alarmStatusList?: boolean;
}

export const MASTER_MAP: Record<keyof I_MasterListNames, keyof C_Unit> = {
  allergyList: 'allergyList',
  altableList: 'altableList',
  destinationList: 'destinationList',
  accessControlList: 'accessControlList',
  diagnosisList: 'diagnosisList',
  doctorList: 'doctorList',
  doctorTypeList: 'doctorTypeList',
  insulationList: 'insulationList',
  mobilityList: 'mobilityList',
  originList: 'originList',
  patientStatusList: 'patientStatusList',
  languageBarrierList: 'languageBarrierList',
  passiveSittingList: 'passiveSittingList',
  procedureList: 'procedureList',
  testList: 'testList',
  serviceList: 'serviceList',
  therapeuticCeilingList: 'therapeuticCeilingList',
  treatmentList: 'treatmentList',
  visitList: 'visitList',
  genericList: 'genericList',
  carePlanObjectiveList: 'carePlanObjectiveList',
  episList: 'episList',
  hobbiesList: 'hobbiesList',
  catheterList: 'catheterList', // Sondas
  clinicalRouteList: 'clinicalRouteList', // Vías
  clinicalRouteLocationList: 'clinicalRouteLocationList',
  nurseCallList: 'nurseCallList',
  alarmStatusList: 'alarmStatusList',
};
